import { mergeHistory, withItemHistory } from "../domain/previousPerformance";
import {
  openWorkoutLogDb,
  WORKOUT_LOG_STORES,
} from "./dbSchema";
import { reconcileExecutionRecord } from "../domain/executionReconciliation";
import { syncConflictMessage } from "../domain/syncIssue";

const TERMINAL_STATUSES = new Set(["SAVED", "DISCARDED"]);
const ACKNOWLEDGED_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function assertUserId(userId) {
  if (!userId || typeof userId !== "string") {
    throw new TypeError("A userId is required for every workout-log store operation.");
  }
}

function normalizeExecution(record) {
  if (!record?.executionId) throw new TypeError("executionId is required");
  assertUserId(record.userId);
  const localRevision = Number(record.localRevision || 0);
  const ackedRevision = Number(record.ackedRevision || 0);
  const pending = localRevision > ackedRevision;
  return {
    ...record,
    recordSchemaVersion: record.recordSchemaVersion || 1,
    localRevision,
    ackedRevision,
    pending,
    pendingKey: pending ? 1 : 0,
    status: record.document?.status || record.status || "ACTIVE",
  };
}

function acknowledgementTime(record) {
  const value = record.lastAcknowledgedAt || record.result?.serverSavedAt ||
    record.document?.savedAt || record.document?.discardedAt;
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(time) ? time : null;
}

// Phase 6E.1: a local-only integer bumped inside every preference write
// transaction. It never reaches the server and, unlike `updatedAt`, two writes
// in the same millisecond still get distinct values. Records written before
// 6E.1 read as 0.
function preferenceChangeToken(record) {
  return Number(record?.localChangeToken) || 0;
}

function isPending(record) {
  return Number(record?.localRevision || 0) > Number(record?.ackedRevision || 0);
}

function quarantineCopy(record, reason, { confirmed = false } = {}) {
  return {
    userId: record.userId,
    executionId: record.executionId,
    reason,
    document: record.document,
    localRevision: record.localRevision,
    leaseEpoch: record.leaseEpoch,
    confirmed,
    createdAt: new Date().toISOString(),
  };
}

function quarantinedWriteError(reason) {
  const error = new Error(syncConflictMessage(reason));
  error.code = reason || "EXECUTION_QUARANTINED";
  error.quarantined = true;
  return error;
}

function withDurablePickerState(record, durable) {
  const { pickerShowAll: _ignored, ...rest } = record;
  return durable.pickerShowAll === undefined
    ? rest
    : { ...rest, pickerShowAll: durable.pickerShowAll };
}

// History (§13.1) is server-provided context kept on the local record. Like
// the picker state it is written only by its own local-only method, so an
// execution write from a stale editor never drops an entry stored meanwhile.
function withDurableHistory(record, durable) {
  if (!record?.server) return record;
  const history = mergeHistory(record.server.history, durable?.server?.history);
  return { ...record, server: { ...record.server, history } };
}

export class LocalExecutionStore {
  constructor({ openDb = openWorkoutLogDb } = {}) {
    this.openDb = openDb;
    this.preferenceListeners = new Map();
  }

  async getMeta(key) {
    const db = await this.openDb();
    return db.get(WORKOUT_LOG_STORES.META, key);
  }

  async setMeta(key, value) {
    const db = await this.openDb();
    await db.put(WORKOUT_LOG_STORES.META, value, key);
    return value;
  }

  async setLastAuthenticatedUserId(userId) {
    assertUserId(userId);
    return this.setMeta("lastAuthenticatedUserId", userId);
  }

  async getLastAuthenticatedUserId() {
    return this.getMeta("lastAuthenticatedUserId");
  }

  // Only after a successful server logout (§11.6). The hint is removed only
  // while it still names that user: another tab may have authenticated since.
  async clearLastAuthenticatedUserId(userId) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.META, "readwrite");
    const meta = transaction.objectStore(WORKOUT_LOG_STORES.META);
    const cleared = (await meta.get("lastAuthenticatedUserId")) === userId;
    if (cleared) await meta.delete("lastAuthenticatedUserId");
    await transaction.done;
    return cleared;
  }

  async putExecution(record) {
    const normalized = normalizeExecution(record);
    const db = await this.openDb();
    const transaction = db.transaction(
      [WORKOUT_LOG_STORES.EXECUTIONS, WORKOUT_LOG_STORES.QUARANTINE],
      "readwrite"
    );
    const store = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
    const quarantine = transaction.objectStore(WORKOUT_LOG_STORES.QUARANTINE);
    const current = await store.get(normalized.executionId);
    const confirmedQuarantine = (await quarantine.index("byUser").getAll(normalized.userId))
      .find((entry) => entry.executionId === normalized.executionId &&
        entry.confirmed === true &&
        Number(normalized.leaseEpoch || 0) <= Number(entry.leaseEpoch || 0));
    if (confirmedQuarantine) {
      await transaction.done;
      throw quarantinedWriteError(confirmedQuarantine.reason);
    }
    if (
      current?.userId === normalized.userId &&
      Number(current.leaseEpoch || 0) !== Number(normalized.leaseEpoch || 0)
    ) {
      const losingRecord = Number(current.leaseEpoch || 0) > Number(normalized.leaseEpoch || 0)
        ? normalized
        : current;
      if (isPending(losingRecord)) {
        await quarantine.add(quarantineCopy(losingRecord, "LEASE_LOST"));
      }
    }
    const sameUserCurrent = current?.userId === normalized.userId;
    const canonical = sameUserCurrent
      ? reconcileExecutionRecord(normalized, current)
      : normalized;
    if (sameUserCurrent && canonical === current) {
      // The durable record outranks this write (a stale editor, a terminal
      // record, or an equal revision): leave IndexedDB untouched and hand the
      // canonical record back so the caller adopts it instead of its candidate.
      await transaction.done;
      return current;
    }
    // Session-local picker state (§15.8 Show all) is written only by
    // setPickerShowAll; an execution write never replaces the durable value.
    const reconciled = normalizeExecution(sameUserCurrent
      ? withDurableHistory(withDurablePickerState(canonical, current), current)
      : canonical);
    await store.put(reconciled);
    await transaction.done;
    return reconciled;
  }

  async getExecution(userId, executionId) {
    assertUserId(userId);
    const db = await this.openDb();
    const record = await db.get(WORKOUT_LOG_STORES.EXECUTIONS, executionId);
    return record?.userId === userId ? record : null;
  }

  async listExecutions(userId, { status, pending } = {}) {
    assertUserId(userId);
    const db = await this.openDb();
    if (typeof pending === "boolean") {
      return db.getAllFromIndex(
        WORKOUT_LOG_STORES.EXECUTIONS,
        "byUserPending",
        [userId, pending ? 1 : 0]
      );
    }
    if (status) {
      return db.getAllFromIndex(
        WORKOUT_LOG_STORES.EXECUTIONS,
        "byUserStatus",
        [userId, status]
      );
    }
    return db.getAllFromIndex(WORKOUT_LOG_STORES.EXECUTIONS, "byUser", userId);
  }

  async getPendingExecutions(userId) {
    return this.listExecutions(userId, { pending: true });
  }

  async hasUnacknowledged(userId) {
    assertUserId(userId);
    const db = await this.openDb();
    const key = await db.getKeyFromIndex(
      WORKOUT_LOG_STORES.EXECUTIONS,
      "byUserPending",
      [userId, 1]
    );
    return key !== undefined;
  }

  async updateExecution(userId, executionId, updater) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.EXECUTIONS, "readwrite");
    const store = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
    const current = await store.get(executionId);
    if (!current || current.userId !== userId) {
      await transaction.done;
      return null;
    }
    const updated = await updater(current);
    if (!updated) {
      await transaction.done;
      return current;
    }
    const normalized = normalizeExecution({ ...updated, executionId, userId });
    await store.put(normalized);
    await transaction.done;
    return normalized;
  }

  // Session-level Show all of the execution's picker (§15.8). Local only: no
  // revision, no activity, no sync; it never enters the sync document.
  async setPickerShowAll(userId, executionId, showAll) {
    return this.updateExecution(userId, executionId, (current) => (
      (current.pickerShowAll === true) === Boolean(showAll)
        ? null
        : { ...current, pickerShowAll: Boolean(showAll) }
    ));
  }

  // LAST / Suggested inputs of one added or substituted item (§13.1). Local
  // only: no revision, no activity, no sync; it never enters the sync document.
  async setItemHistory(userId, executionId, itemId, entry) {
    if (!itemId || !entry) return this.getExecution(userId, executionId);
    return this.updateExecution(userId, executionId, (current) => (
      current.server
        ? {
            ...current,
            server: {
              ...current.server,
              history: withItemHistory(current.server.history, itemId, entry),
            },
          }
        : null
    ));
  }

  async deleteAcknowledgedExecution(userId, executionId) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.EXECUTIONS, "readwrite");
    const store = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
    const record = await store.get(executionId);
    if (!record || record.userId !== userId) {
      await transaction.done;
      return false;
    }
    if (record.localRevision > record.ackedRevision || record.pending) {
      await transaction.done;
      const error = new Error("Unacknowledged workout data cannot be deleted.");
      error.code = "PENDING_WORKOUT_DATA";
      throw error;
    }
    await store.delete(executionId);
    await transaction.done;
    return true;
  }

  async preserveRejectedExecution(sentRecord, reason) {
    const rejected = normalizeExecution(sentRecord);
    const db = await this.openDb();
    const transaction = db.transaction(
      [WORKOUT_LOG_STORES.EXECUTIONS, WORKOUT_LOG_STORES.QUARANTINE],
      "readwrite"
    );
    const executions = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
    const quarantine = transaction.objectStore(WORKOUT_LOG_STORES.QUARANTINE);
    const current = await executions.get(rejected.executionId);
    await quarantine.add({
      ...quarantineCopy(rejected, reason),
    });
    let preserved = null;
    if (current?.userId === rejected.userId) {
      preserved = normalizeExecution({
        ...current,
        syncState: "CONFLICT",
        lastSyncError: { code: reason },
      });
      await executions.put(preserved);
    }
    await transaction.done;
    return preserved;
  }

  async confirmServerQuarantine(sentRecord, reason) {
    const rejected = normalizeExecution(sentRecord);
    const db = await this.openDb();
    const transaction = db.transaction(
      [WORKOUT_LOG_STORES.EXECUTIONS, WORKOUT_LOG_STORES.QUARANTINE],
      "readwrite"
    );
    const executions = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS);
    const quarantine = transaction.objectStore(WORKOUT_LOG_STORES.QUARANTINE);
    const current = await executions.get(rejected.executionId);
    const sameUserCurrent = current?.userId === rejected.userId;
    const rejectedEpoch = Number(rejected.leaseEpoch || 0);
    const currentEpoch = Number(current?.leaseEpoch || 0);

    if (reason === "LEASE_LOST" && sameUserCurrent && currentEpoch > rejectedEpoch) {
      await quarantine.add(quarantineCopy(rejected, reason, { confirmed: true }));
      await transaction.done;
      return { record: current, invalidatedCurrent: false };
    }

    if (reason === "LEASE_LOST" && sameUserCurrent && currentEpoch < rejectedEpoch) {
      if (isPending(current)) {
        await quarantine.add(quarantineCopy(current, reason));
      }
      await quarantine.add(quarantineCopy(rejected, reason, { confirmed: true }));
    } else {
      const preserved = sameUserCurrent ? current : rejected;
      await quarantine.add(quarantineCopy(preserved, reason, { confirmed: true }));
    }
    if (sameUserCurrent) await executions.delete(rejected.executionId);
    await transaction.done;
    return { record: null, invalidatedCurrent: true };
  }

  async listQuarantine(userId) {
    assertUserId(userId);
    const db = await this.openDb();
    return db.getAllFromIndex(WORKOUT_LOG_STORES.QUARANTINE, "byUser", userId);
  }

  // Records that the athlete has seen the quarantine notice. The quarantined
  // copy itself, and its confirmed-quarantine tombstone role, are untouched.
  async acknowledgeQuarantineNotices(userId, ids, { now = new Date() } = {}) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.QUARANTINE, "readwrite");
    const quarantine = transaction.objectStore(WORKOUT_LOG_STORES.QUARANTINE);
    let acknowledged = 0;
    for (const id of ids || []) {
      const entry = await quarantine.get(id);
      if (entry?.userId === userId && !entry.noticeAcknowledgedAt) {
        await quarantine.put({ ...entry, noticeAcknowledgedAt: now.toISOString() });
        acknowledged += 1;
      }
    }
    await transaction.done;
    return acknowledged;
  }

  async purgeAcknowledged(userId, {
    now = Date.now(),
    olderThanMs = null,
    terminalOnly = false,
    includePreferences = false,
  } = {}) {
    assertUserId(userId);
    const db = await this.openDb();
    const stores = includePreferences
      ? [WORKOUT_LOG_STORES.EXECUTIONS, WORKOUT_LOG_STORES.PREFERENCES]
      : WORKOUT_LOG_STORES.EXECUTIONS;
    const transaction = db.transaction(stores, "readwrite");
    const index = transaction.objectStore(WORKOUT_LOG_STORES.EXECUTIONS).index("byUser");
    let cursor = await index.openCursor(userId);
    let deleted = 0;
    while (cursor) {
      const record = cursor.value;
      const acknowledged = !record.pending && record.localRevision <= record.ackedRevision;
      const terminal = TERMINAL_STATUSES.has(record.document?.status || record.status);
      const acknowledgedAt = acknowledgementTime(record);
      const oldEnough = olderThanMs == null ||
        (acknowledgedAt != null && acknowledgedAt <= now - olderThanMs);
      if (acknowledged && (!terminalOnly || terminal) && oldEnough) {
        await cursor.delete();
        deleted += 1;
      }
      cursor = await cursor.continue();
    }
    if (includePreferences) {
      const preferences = transaction.objectStore(WORKOUT_LOG_STORES.PREFERENCES);
      const cached = await preferences.get(userId);
      if (cached && !cached.pendingPatch) await preferences.delete(userId);
    }
    await transaction.done;
    return deleted;
  }

  async purgeAfterLogout(userId) {
    return this.purgeAcknowledged(userId, { includePreferences: true });
  }

  async purgeExpiredAcknowledged(userId, options = {}) {
    return this.purgeAcknowledged(userId, {
      ...options,
      olderThanMs: ACKNOWLEDGED_RETENTION_MS,
      terminalOnly: true,
    });
  }

  // Phase 6E/6E.1: the preference cache is observable per user (same tab), so
  // a mounted useWorkoutPreferences sees CONFIRMED preferences without
  // remounting. Exactly two writers publish, after their transaction commits:
  //   - cachePreferences: a server response (Settings save, preference GET,
  //     start response, first-use PATCH);
  //   - acknowledgePreferences: the server's answer to a flushed queued patch.
  // A publication carries only those server-confirmed values, never a queued
  // pendingPatch merged into the record. queuePreferencesPatch never publishes.
  subscribePreferences(userId, listener) {
    assertUserId(userId);
    const listeners = this.preferenceListeners.get(userId) || new Set();
    listeners.add(listener);
    this.preferenceListeners.set(userId, listeners);
    return () => {
      listeners.delete(listener);
      if (!listeners.size && this.preferenceListeners.get(userId) === listeners) {
        this.preferenceListeners.delete(userId);
      }
    };
  }

  notifyPreferences(userId, publication) {
    [...(this.preferenceListeners.get(userId) || [])].forEach((listener) => {
      try {
        listener(publication);
      } catch (_error) {
        // A consumer's failure never fails the cache write or other consumers.
      }
    });
  }

  async getPreferences(userId) {
    assertUserId(userId);
    const db = await this.openDb();
    return (await db.get(WORKOUT_LOG_STORES.PREFERENCES, userId)) || null;
  }

  // `ifChangeToken` makes the write conditional (Phase 6E.1): it is skipped,
  // returning null, unless the record still carries the token the caller read
  // before its request. The compare and the put share one readwrite
  // transaction, which IndexedDB serializes against every other preference
  // write, so a newer publication can never land between them.
  async cachePreferences(userId, preferences, { ifChangeToken } = {}) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.PREFERENCES, "readwrite");
    const store = transaction.objectStore(WORKOUT_LOG_STORES.PREFERENCES);
    const current = await store.get(userId);
    if (ifChangeToken !== undefined && preferenceChangeToken(current) !== ifChangeToken) {
      await transaction.done;
      return null;
    }
    const pendingPatch = current?.pendingPatch || null;
    const next = {
      userId,
      preferences: { ...(preferences || {}), ...(pendingPatch || {}) },
      pendingPatch,
      localRevision: current?.localRevision || 0,
      ackedRevision: current?.ackedRevision || 0,
      localChangeToken: preferenceChangeToken(current) + 1,
      updatedAt: new Date().toISOString(),
    };
    await store.put(next);
    await transaction.done;
    this.notifyPreferences(userId, {
      preferences: { ...(preferences || {}) },
      hasPendingPatch: Boolean(pendingPatch),
    });
    return next;
  }

  async queuePreferencesPatch(userId, patch) {
    assertUserId(userId);
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
      throw new TypeError("A workout preferences patch is required.");
    }
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.PREFERENCES, "readwrite");
    const store = transaction.objectStore(WORKOUT_LOG_STORES.PREFERENCES);
    const current = await store.get(userId) || {
      userId,
      preferences: {},
      pendingPatch: null,
      localRevision: 0,
      ackedRevision: 0,
    };
    const next = {
      ...current,
      preferences: { ...current.preferences, ...patch },
      pendingPatch: { ...(current.pendingPatch || {}), ...patch },
      localRevision: current.localRevision + 1,
      localChangeToken: preferenceChangeToken(current) + 1,
      updatedAt: new Date().toISOString(),
    };
    await store.put(next);
    await transaction.done;
    // Locally durable for first-use recovery, but unconfirmed: not published.
    return next;
  }

  async acknowledgePreferences(userId, revision, serverPreferences) {
    assertUserId(userId);
    const db = await this.openDb();
    const transaction = db.transaction(WORKOUT_LOG_STORES.PREFERENCES, "readwrite");
    const store = transaction.objectStore(WORKOUT_LOG_STORES.PREFERENCES);
    const current = await store.get(userId);
    if (!current) {
      await transaction.done;
      return null;
    }
    const hasNewerLocalPatch = current.localRevision > revision;
    const next = {
      ...current,
      preferences: hasNewerLocalPatch
        ? { ...serverPreferences, ...(current.pendingPatch || {}) }
        : { ...serverPreferences },
      pendingPatch: hasNewerLocalPatch ? current.pendingPatch : null,
      ackedRevision: Math.max(current.ackedRevision || 0, revision),
      localChangeToken: preferenceChangeToken(current) + 1,
      updatedAt: new Date().toISOString(),
    };
    await store.put(next);
    await transaction.done;
    // The server's response is canonical; a newer queued patch still merged
    // into the record is not.
    this.notifyPreferences(userId, {
      preferences: { ...serverPreferences },
      hasPendingPatch: hasNewerLocalPatch,
    });
    return next;
  }
}

export class ExecutionWriteCoordinator {
  constructor({
    store,
    onError = () => {},
    windowObject = typeof window === "undefined" ? null : window,
    documentObject = typeof document === "undefined" ? null : document,
  } = {}) {
    if (!store) throw new TypeError("A LocalExecutionStore is required.");
    this.store = store;
    this.onError = onError;
    this.windowObject = windowObject;
    this.documentObject = documentObject;
    this.pendingRecord = null;
    this.waiters = [];
    this.frameId = null;
    this.deadlineId = null;
    this.flushPromise = null;
    this.blockedExecutions = new Map();
    this.handleVisibility = () => {
      if (this.documentObject?.visibilityState === "hidden") {
        this.flush().catch(this.onError);
      }
    };
    this.handlePageHide = () => this.flush().catch(this.onError);
    this.documentObject?.addEventListener("visibilitychange", this.handleVisibility);
    this.windowObject?.addEventListener("pagehide", this.handlePageHide);
  }

  commit(record) {
    const blocked = this.blockedExecutions.get(record?.executionId);
    if (blocked) return Promise.reject(blocked);
    this.pendingRecord = record;
    const promise = new Promise((resolve, reject) => {
      this.waiters.push({ resolve, reject });
    });
    this.schedule();
    return promise;
  }

  hasPendingWrites() {
    return Boolean(this.pendingRecord || this.flushPromise);
  }

  invalidateExecution(executionId, error = quarantinedWriteError()) {
    if (!executionId) return;
    this.blockedExecutions.set(executionId, error);
    if (this.pendingRecord?.executionId !== executionId) return;
    this.pendingRecord = null;
    this.cancelScheduled();
    const waiters = this.waiters;
    this.waiters = [];
    waiters.forEach(({ reject }) => reject(error));
  }

  schedule() {
    if (this.frameId == null && this.windowObject?.requestAnimationFrame) {
      this.frameId = this.windowObject.requestAnimationFrame(() => {
        this.frameId = null;
        this.flush().catch(this.onError);
      });
    }
    if (this.deadlineId == null) {
      this.deadlineId = setTimeout(() => {
        this.deadlineId = null;
        this.flush().catch(this.onError);
      }, 150);
    }
  }

  cancelScheduled() {
    if (this.frameId != null && this.windowObject?.cancelAnimationFrame) {
      this.windowObject.cancelAnimationFrame(this.frameId);
    }
    if (this.deadlineId != null) clearTimeout(this.deadlineId);
    this.frameId = null;
    this.deadlineId = null;
  }

  async flush() {
    this.cancelScheduled();
    if (this.flushPromise) return this.flushPromise;
    this.flushPromise = (async () => {
      while (this.pendingRecord) {
        const record = this.pendingRecord;
        const waiters = this.waiters;
        this.pendingRecord = null;
        this.waiters = [];
        try {
          const persisted = await this.store.putExecution(record);
          waiters.forEach(({ resolve }) => resolve(persisted));
        } catch (error) {
          if (!this.pendingRecord && !this.blockedExecutions.has(record.executionId)) {
            this.pendingRecord = record;
          }
          waiters.forEach(({ reject }) => reject(error));
          this.onError(error);
          throw error;
        }
      }
    })();
    try {
      return await this.flushPromise;
    } finally {
      this.flushPromise = null;
      if (this.pendingRecord) this.schedule();
    }
  }

  dispose() {
    this.cancelScheduled();
    this.documentObject?.removeEventListener("visibilitychange", this.handleVisibility);
    this.windowObject?.removeEventListener("pagehide", this.handlePageHide);
  }
}

export const workoutLogStore = new LocalExecutionStore();

export { ACKNOWLEDGED_RETENTION_MS };
